import React, { useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useSearchParams } from 'react-router-dom';

import PaymentDetails from './PaymentDetails';
import ProfileDetails from './ProfileDetails';
import SignInDetails from './SignInDetails';

import { useConsumerStatusSummaryQuery } from 'apollo/queries/autogenerate/hooks';
import {
  ACTION,
  ITEM,
  SCREEN,
} from 'shared/modules/account/constants/mixpanel';
import Button from 'shared/modules/common/components/atoms/Button';
import useIsWebEKYCEnabled from 'shared/modules/common/hooks/use-is-web-ekyc-enabled';
import Status from 'shared/modules/ekyc/components/organism/Status';
import eventBus from 'shared/utils/event-bus';
import Mixpanel from 'shared/vendor/Mixpanel';
import Footer from 'web/modules/common/components/organisms/Footer';

const AccountScreen = () => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });

  const openInNewTab = (url: string) => window.open(url, '_blank');

  // billId is passed as prop to Payment Details screen which then displays settlement method details for specific bill.
  const billId = useSearchParams()[0].get('bill_id');

  const { data } = useConsumerStatusSummaryQuery();
  const isWebKYCEnabled = useIsWebEKYCEnabled();
  const kycStatus = data?.KYCApplicationStatus?.status;
  const houkatsuStatus = data?.houkatsuStatus.status;

  useEffect(() => {
    const eventName = `${SCREEN.ACCOUNT} - ${ACTION.PAGE_VIEW}`;

    Mixpanel.timeEvent(eventName);

    return () => {
      Mixpanel.trackEvent(eventName);
    };
  }, []);

  return (
    <View style={[styles.screen, isBigScreen && styles.screenBigScreen]}>
      <View
        style={[styles.container, isBigScreen && styles.containerBigScreen]}
      >
        {isWebKYCEnabled && kycStatus && houkatsuStatus && (
          <>
            <Text style={styles.sectionTitle}>ステータス情報</Text>
            <View style={styles.section}>
              <Status
                kycStatus={kycStatus}
                houkatsuStatus={houkatsuStatus}
                onVerifyIdentityButtonPress={() => {
                  const eventName = `${SCREEN.ACCOUNT} - ${ACTION.CLICK}`;

                  Mixpanel.trackEvent(eventName, {
                    Item: 'Start eKYC Onboarding',
                  });

                  eventBus.emit('ekyc-onboarding:open');
                }}
              />
            </View>
          </>
        )}

        <Text style={styles.sectionTitle}>ログイン情報</Text>
        <View style={styles.section}>
          <SignInDetails />
        </View>
        <Text style={styles.sectionTitle}>お客様情報</Text>
        <View style={styles.section}>
          <ProfileDetails />
        </View>
        <Text style={styles.sectionTitle}>お支払い方法の選択</Text>
        <PaymentDetails billId={billId} />
        {!isBigScreen && (
          <View style={[styles.section, styles.marginBottomNone]}>
            <Button
              title="ヘルプ"
              variant="outlined"
              onPress={() => {
                Mixpanel.trackEvent(`${SCREEN.ACCOUNT} - ${ACTION.CLICK}`, {
                  Item: ITEM.HELP,
                });

                openInNewTab('https://cs.paidy.com');
              }}
              style={styles.button}
              id="account-button-help"
            />
            <Button
              title="利用規約・同意書一覧"
              variant="outlined"
              onPress={() => {
                Mixpanel.trackEvent(`${SCREEN.ACCOUNT} - ${ACTION.CLICK}`, {
                  Item: ITEM.TERMS_AND_CONDITIONS,
                });

                openInNewTab('https://terms.paidy.com');
              }}
              style={styles.button}
              id="account-button-terms"
            />
            <Button
              title="ログアウト"
              variant="outlined"
              onPress={() => {
                eventBus.emit('signout:open');
              }}
              id="account-button-signout"
            />
          </View>
        )}
        <Footer />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  // Template for building a new main screen
  // Scrolls on overflow for both mobile and desktop
  screen: {
    alignItems: 'center',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    overflow: 'scroll',
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  screenBigScreen: {
    paddingHorizontal: 24,
  },
  container: {
    width: '100%',
  },
  containerBigScreen: {
    maxWidth: 560,
  },
  sectionTitle: {
    color: '#607D8B',
    fontWeight: 'bold',
    marginBottom: 16,
  },
  section: {
    backgroundColor: 'white',
    padding: 24,
    borderRadius: 24,
    marginBottom: 24,
  },
  marginBottomNone: {
    marginBottom: 0,
  },
  button: {
    marginBottom: 16,
  },
});

export default AccountScreen;
